import React, { useEffect, useMemo, useRef } from 'react'
import { animated, useSpring, easings } from '@react-spring/web'
import MarkdownIt from 'markdown-it'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'
import useRefDimensions from '../../hooks/useRefDimensions'

import { ActionType, CurrentSubView, UIPart } from '../../models/enums'

import SvgColorChanger from './Utils/SvgColorChanger'

interface GridAttachmentProps {
  index: number
  text: string
  textColor?: string
  backgroundColor?: string
  image?: {
    url: string
    color?: string
  }
  action: {
    type: string
    value?: Record<string, any>
  }
  history?: boolean
  setQrCodeDataOverlay?: (url: string) => void
}

const springEnterDuration = 1000

const md = new MarkdownIt({
  breaks: true,
  html: true,
  xhtmlOut: true,
  typographer: true,
  quotes: `""''`
})

const GridAttachment = ({ index, text, textColor, backgroundColor, image, action, history, setQrCodeDataOverlay }: GridAttachmentProps): JSX.Element => {
  const dispatch = useDispatch()
  const isUsedOnBorne = useSelector((state: RootState) => state.retorikReducer.configuration.isUsedOnBorne)
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)

  const divRef = useRef<HTMLDivElement>(null)
  const tileDimensions = useRefDimensions(divRef)

  const height = useMemo<number>(() => {
    return tileDimensions.width * 0.75
  }, [tileDimensions.width])

  const showImage = useMemo<boolean>(() => {
    return !!(image?.url && height > 80)
  }, [height, image])

  const nbLines = useMemo<number>(() => {
    if (!showImage) {
      const remToPx = parseFloat(getComputedStyle(document.documentElement).fontSize)
      const lineHeightInPx = remToPx * (isMobile ? 1 : 1.1)

      return Math.floor(height / lineHeightInPx)
    }

    return 2
  }, [showImage, height])

  const lineClamp = useMemo<string>(() => {
    return showImage ? 'rf-h-2/5 rf-line-clamp-2' : `rf-line-clamp-${nbLines}`
  }, [nbLines, showImage])

  const maxHeight = useMemo<string>(() => {
    return showImage ? 'rf-max-h-8 large:rf-max-h-[2.3rem]' : `rf-max-h-[${nbLines}rem] large:rf-max-h-[${nbLines * 1.1 + 0.1}rem]`
  }, [nbLines, showImage])

  const [spring, api] = useSpring(() => ({
    from: {
      transform: 'translateX(0%)',
      opacity: 0
    }
  }))

  useEffect(() => {
    setTimeout(() => {
      api.start({
        from: {
          transform: 'translateX(100%)',
          opacity: 0
        },
        to: {
          transform: 'translateX(0%)',
          opacity: 1
        },
        config: {
          duration: springEnterDuration,
          easing: easings.easeInBack
        }
      })
    }, index * 200)
  }, [])

  const handleInternalAction = (value: Record<string, any> | undefined): void => {
    if (value?.name) {
      switch (value.name.toLowerCase()) {
        case 'openmenu':
          dispatch(storeActions.utils.setDashboardOpened(true))
          break
        case 'openlanguage':
          dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.languages))
          break
        case 'openhistory':
          dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.history))
          break
        case 'opentutorial':
          console.log('Open tutorial')
          break
        case 'fullscreenimage':
          value.url && dispatch(storeActions.utils.setFullScreenImage(value.url))
          break
      }
    }
  }

  const handleClick = (e: React.MouseEvent): void => {
    switch (action.type) {
      case ActionType.message:
        action.value?.text && dispatch(storeActions.directline.sendMessage({ text: action.value.text }))
        break
      case ActionType.event:
        action.value?.name && dispatch(storeActions.directline.sendEvent({ name: action.value.name, value: action.value.value || null }))
        break
      case ActionType.url:
        if (action.value?.url) {
          // Generate QR-code data on borne, open the URL otherwise
          !isMobile && isUsedOnBorne ? setQrCodeDataOverlay?.(action.value.url) : window.open(action.value.url, '_blank', 'noopener noreferrer')
        }
        break
      case ActionType.internal:
        handleInternalAction(action.value)
        break
    }

    dispatch(storeActions.utils.setClickedOnUI(UIPart.card))
    e.stopPropagation()
    e.preventDefault()
  }

  return (
    <animated.div
      ref={divRef}
      className={`rf-relative rf-w-full ${history ? 'rf-p-3 rf-pointer-events-none' : 'rf-p-4'} rf-flex rf-flex-col ${
        image?.url ? 'rf-justify-between' : 'rf-justify-end'
      } rf-items-start rf-gap-[10%] ${
        isMobile ? 'rf-border rf-border-[#F0F0F0]' : ''
      } hover:rf-shadow-[inset_0_0_12px_#00000029] hover:rf-cursor-pointer rf-rounded rf-shadow-[-3px_-3px_10px_#00000099] rf-overflow-hidden`}
      style={{
        height: `calc(${height}px + 1.5rem)`,
        color: textColor || '#575F6B',
        backgroundColor: backgroundColor || '#FFF',
        ...spring
      }}
      onClick={(e) => handleClick(e)}
    >
      {/* Image */}
      {showImage && (
        <div className='rf-h-1/2'>
          {image?.url && image.url.indexOf('svg') > -1 ? <SvgColorChanger svgUrl={image.url} height='100%' fillColor={image.color} /> : <img className='rf-h-full' src={image?.url} />}
        </div>
      )}

      {/* Text */}
      <div className={`rf-flex rf-min-h-8 large:rf-min-h-[2.3rem] ${lineClamp} rf-items-start rf-text-small-size-auto rf-overflow-y-hidden`}>
        <p
          className={`${maxHeight} rf-overflow-y-clip`}
          dangerouslySetInnerHTML={{
            __html: md.renderInline(text)
          }}
        />
      </div>
    </animated.div>
  )
}

export default GridAttachment
